import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import { duplicateNameFromError } from "../api/duplicateName";
import type { ExistingItemRef } from "../api/types";
import { DuplicateNameDialog } from "../components/DuplicateNameDialog";
import type {
  CatalogItem,
  Color,
  DefaultUom,
  ItemCategory,
  Location,
  SubCategory,
} from "../api/types";
import { CREATE_NEW_CATEGORY } from "../components/CategoryPicker";
import { NewCategoryFields } from "../components/NewCategoryFields";
import { PhotoInput } from "../components/PhotoInput";
import { BarcodeField } from "../components/BarcodeField";
import { useAuth } from "../state/AuthContext";
import { WizardShell } from "./item-create/WizardShell";
import {
  EMPTY_DRAFT,
  WIZARD_STEPS,
  buildCreateItemPayload,
  categoryDefaults,
  firstRefusal,
  showsVesselDimensions,
  validateStep,
  type ItemDraft,
  type WizardStep,
} from "./item-create/itemDraft";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-90 — creating an item is six screens, not one wall of fields
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This was a single `.form-grid` card: up to seventeen inputs at once, of which
 * exactly two are required, in two ~150 px columns on a phone, with every
 * validation message waiting until submit. The owner opened it on their phone
 * and the columns were the first thing they named.
 *
 * Now: one question at a time, the category first because it decides the rest
 * (a flower earns a flower-type step and is counted by the stem; a vase earns
 * its dimensions), single-choice screens advance on the tap itself, and each
 * step refuses on its own terms instead of hoarding the refusal for the end.
 *
 * What did NOT change: the payload. `buildCreateItemPayload` produces exactly
 * what this page built before, and `api.createItem` / `createItemWithImage`
 * are untouched. No endpoint, no DTO, no migration.
 *
 * The rules live in `item-create/itemDraft.ts` (and are tested there); the
 * chrome lives in `item-create/WizardShell.tsx`; this file asks the questions.
 */

const UOMS: DefaultUom[] = ["UNIT", "STEM", "CM", "ROLL"];

const UOM_WORD: Record<DefaultUom, string> = {
  UNIT: "unit",
  STEM: "stem",
  CM: "cm",
  ROLL: "roll",
};

export function ItemCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, hasRole } = useAuth();
  const returnLocationId = searchParams.get("returnLocation")?.trim() ?? "";

  const [step, setStep] = useState<WizardStep | "done">("category");
  const [furthestIndex, setFurthestIndex] = useState(0);
  /**
   * G-94 — what the inventory page was looking for when it found nothing.
   *
   * A scan that matches no item there offers to create one, and the code (or
   * the typed name) travels in the URL so nobody retypes it. Seeded ONCE into
   * the first draft rather than synced: from here on the person is editing
   * these fields, and a URL that keeps re-asserting itself would undo them.
   *
   * The wizard still opens on its first question — category — because the seed
   * answers neither. Nothing is created until the person walks the six screens
   * and presses the button at the end.
   */
  const [draft, setDraft] = useState<ItemDraft>(() => {
    const barcode = searchParams.get("barcode")?.trim() ?? "";
    const name = searchParams.get("name")?.trim() ?? "";
    if (!barcode && !name) return EMPTY_DRAFT;
    return { ...EMPTY_DRAFT, barcode, name };
  });
  const [stepError, setStepError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CatalogItem | null>(null);
  const [extrasOpen, setExtrasOpen] = useState(false);

  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [categoryListVersion, setCategoryListVersion] = useState(0);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Where "Receive in the shop" would put the stock. Known when the create was
  // opened from a shop, or when the user has one of their own (G-83's rule);
  // an owner looking at every shop at once has to name one, because guessing
  // would put stock in the wrong shop.
  const scopedLocationId = returnLocationId || user?.assignedLocation?.id || "";
  const [receiveLocationId, setReceiveLocationId] = useState(scopedLocationId);
  const [locations, setLocations] = useState<Location[]>([]);

  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const creatingNewCategory = draft.categoryId === CREATE_NEW_CATEGORY;
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === draft.categoryId),
    [categories, draft.categoryId],
  );
  const selectedCategoryCode = selectedCategory?.code;
  // Owner-only, mirroring `CategoryPicker`'s gate and the API's own (G-30):
  // creating a category means setting a tax rate.
  const canCreateCategory = hasRole("OWNER");

  const patch = useCallback((next: Partial<ItemDraft>) => {
    setDraft((current) => ({ ...current, ...next }));
  }, []);

  useEffect(() => {
    void api
      .listCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [categoryListVersion]);

  useEffect(() => {
    void api
      .listColors()
      .then(setColors)
      .catch(() => setColors([]));
  }, []);

  useEffect(() => {
    if (!selectedCategoryCode) {
      setSubCategories([]);
      return;
    }
    void api
      .listSubCategories(selectedCategoryCode)
      .then(setSubCategories)
      .catch(() => setSubCategories([]));
  }, [selectedCategoryCode]);

  // Only fetched for the case that needs it: an owner with no shop in scope,
  // standing on the done screen with an item that has nowhere to be received.
  useEffect(() => {
    if (step !== "done" || scopedLocationId || locations.length) return;
    void api
      .listLocations()
      .then(setLocations)
      .catch(() => setLocations([]));
  }, [step, scopedLocationId, locations.length]);

  // A live preview of the chosen photo, so the done screen can lead with the
  // item itself rather than a tick.
  useEffect(() => {
    if (!imageFile) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  // Each screen starts at its own top; without this the second step opens
  // halfway down because the first one was scrolled.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (step === "name") {
      const timer = window.setTimeout(() => nameInputRef.current?.focus(), 180);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [step]);

  const goTo = useCallback(
    (next: WizardStep | "done") => {
      setStepError(null);
      setStep(next);
      if (next !== "done") {
        setFurthestIndex((furthest) => Math.max(furthest, WIZARD_STEPS.indexOf(next)));
      }
    },
    [],
  );

  const advance = useCallback(() => {
    if (step === "done") return;
    const refusal = validateStep(step, draft, creatingNewCategory);
    if (refusal) {
      setStepError(refusal);
      return;
    }
    const index = WIZARD_STEPS.indexOf(step);
    const next = WIZARD_STEPS[index + 1];
    if (next) goTo(next);
  }, [creatingNewCategory, draft, goTo, step]);

  const back = useCallback(() => {
    if (step === "done") return;
    const index = WIZARD_STEPS.indexOf(step);
    if (index > 0) goTo(WIZARD_STEPS[index - 1]);
  }, [goTo, step]);

  function pickCategory(category: ItemCategory) {
    const defaults = categoryDefaults(category.code);
    patch({
      categoryId: category.id,
      subCategoryId: "",
      ...(defaults ?? {}),
    });
    setStepError(null);
    // The tap IS the answer — no Continue to hunt for on a single-choice screen.
    window.setTimeout(() => goTo("name"), 130);
  }

  // G-119: the server refused a second active item with this name; the
  // person sees the twins and either picks one or creates anyway.
  const [duplicatePrompt, setDuplicatePrompt] = useState<{
    name: string;
    existing: ExistingItemRef[];
  } | null>(null);

  async function createItem(confirmDuplicateOf?: string[]) {
    // The rail can carry you back to any step you have reached and forward
    // again without passing its check, so the whole draft is re-checked here
    // rather than trusting that each step was left in a good state. A refusal
    // sends you to the step that owns it.
    const refusal = firstRefusal(draft, creatingNewCategory);
    if (refusal) {
      goTo(refusal.step);
      setStepError(refusal.message);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    let attemptedName = "";
    try {
      let categoryIdForItem = draft.categoryId;
      let categoryCode = selectedCategoryCode;

      if (creatingNewCategory) {
        const newCategory = await api.createCategory({
          label: draft.newCategoryLabel.trim(),
          vatRatePercent: Number(draft.newCategoryVatPercent),
          targetMarginPercent: Number(draft.newCategoryTargetMarginPercent),
        });
        categoryIdForItem = newCategory.id;
        categoryCode = newCategory.code;
        setCategoryListVersion((v) => v + 1);
        // The category now EXISTS. Point the draft at it before anything else
        // can fail: without this, a failed item create leaves the sentinel in
        // place and the next tap on "Create item" mints a second category with
        // the same label.
        patch({ categoryId: newCategory.id });
      }

      const payload = {
        ...buildCreateItemPayload(draft, categoryIdForItem, categoryCode),
        ...(confirmDuplicateOf?.length ? { confirmDuplicateOf } : {}),
      };
      attemptedName = payload.name;
      const item = imageFile
        ? await api.createItemWithImage(payload, imageFile)
        : await api.createItem(payload);
      setDuplicatePrompt(null);
      setCreated(item);
      setStep("done");
    } catch (err: unknown) {
      const twins = duplicateNameFromError(err);
      if (twins) {
        // Shown (or shown again, if a twin appeared meanwhile); the dialog
        // stays until the person answers.
        setDuplicatePrompt({ name: attemptedName, existing: twins });
        return;
      }
      setDuplicatePrompt(null);
      setSubmitError(err instanceof Error ? err.message : "Could not create item");
    } finally {
      setSubmitting(false);
    }
  }

  function startAnother() {
    setDraft(EMPTY_DRAFT);
    setImageFile(null);
    setCreated(null);
    setSubmitError(null);
    setExtrasOpen(false);
    setFurthestIndex(0);
    goTo("category");
  }

  function receiveInShop() {
    if (!created || !receiveLocationId) return;
    /**
     * G-94 S2: create-then-receive now lands on the inventory page, which owns
     * the receive door. The contract is unchanged — same `receiveForStock`
     * param, same "arrive with the modal already open" behaviour — only the
     * page that honours it moved.
     */
    navigate(
      `/app/inventory?locationId=${encodeURIComponent(receiveLocationId)}&receiveForStock=${encodeURIComponent(created.id)}`,
    );
  }

  const unitWord = UOM_WORD[draft.defaultUom];
  const showsDimensions = showsVesselDimensions(selectedCategoryCode);

  /* ── the six screens ──────────────────────────────────────────────────── */

  function renderStep() {
    if (step === "category") {
      return (
        <>
          <div className="iw-tiles">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className="iw-tile"
                aria-pressed={draft.categoryId === category.id}
                onClick={() => pickCategory(category)}
              >
                <span>{category.label}</span>
                {category.code === "FLOWER" ? <small>by the stem</small> : null}
                {showsVesselDimensions(category.code) ? <small>has a size</small> : null}
              </button>
            ))}
            {canCreateCategory ? (
              <button
                type="button"
                className="iw-tile iw-tile--wide iw-tile--new"
                aria-pressed={creatingNewCategory}
                onClick={() => {
                  patch({ categoryId: CREATE_NEW_CATEGORY });
                  setStepError(null);
                }}
              >
                <span>New category…</span>
                <small>Sets its VAT rate and target margin</small>
              </button>
            ) : null}
          </div>

          {creatingNewCategory ? (
            <div className="iw-new-category">
              <NewCategoryFields
                label={draft.newCategoryLabel}
                onLabelChange={(v) => patch({ newCategoryLabel: v })}
                vatPercent={draft.newCategoryVatPercent}
                onVatPercentChange={(v) => patch({ newCategoryVatPercent: v })}
                targetMarginPercent={draft.newCategoryTargetMarginPercent}
                onTargetMarginPercentChange={(v) =>
                  patch({ newCategoryTargetMarginPercent: v })
                }
              />
            </div>
          ) : null}
        </>
      );
    }

    if (step === "name") {
      return (
        <>
          <div className="field">
            <label htmlFor="itemName">Name</label>
            <input
              id="itemName"
              ref={nameInputRef}
              type="text"
              maxLength={200}
              enterKeyHint="next"
              value={draft.name}
              onChange={(e) => {
                patch({ name: e.target.value });
                if (e.target.value.trim()) setStepError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  advance();
                }
              }}
            />
          </div>

          {subCategories.length ? (
            <div className="iw-group">
              <span className="iw-group-label">Flower type</span>
              <div className="iw-chips">
                {subCategories.map((sub) => (
                  <button
                    key={sub.id}
                    type="button"
                    className="iw-chip"
                    aria-pressed={draft.subCategoryId === sub.id}
                    onClick={() =>
                      patch({ subCategoryId: draft.subCategoryId === sub.id ? "" : sub.id })
                    }
                  >
                    {sub.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {colors.length ? (
            <div className="iw-group">
              <span className="iw-group-label">Colour</span>
              <div className="iw-chips">
                {colors.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    className="iw-chip"
                    aria-pressed={draft.colorId === color.id}
                    onClick={() => patch({ colorId: draft.colorId === color.id ? "" : color.id })}
                  >
                    {color.name}
                  </button>
                ))}
              </div>
              <p className="field-hint">
                A single colour for this item. Bouquet colours come from the component items.
              </p>
            </div>
          ) : null}
        </>
      );
    }

    if (step === "photo") {
      return (
        <div className="iw-photo">
          <PhotoInput value={imageFile} onChange={setImageFile} />
          {imageFile ? null : (
            /*
             * The ONLY way past this screen without a photo, and the bar
             * carries no Continue while it is here. Two controls that both mean
             * "move on without a picture" is one control too many — the owner
             * caught exactly that in review. Once there is a photo, PhotoInput's
             * own "Remove photo" undoes it and the bar takes the step forward.
             */
            <button type="button" className="button plain iw-skip" onClick={advance}>
              Skip for now
            </button>
          )}
        </div>
      );
    }

    if (step === "price") {
      return (
        <>
          <div className="iw-group">
            <span className="iw-group-label">Counted by</span>
            <div className="iw-segmented">
              {UOMS.map((uom) => (
                <button
                  key={uom}
                  type="button"
                  aria-pressed={draft.defaultUom === uom}
                  onClick={() => patch({ defaultUom: uom })}
                >
                  {uom}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label htmlFor="salePrice">What you charge (€ per {unitWord})</label>
            <input
              id="salePrice"
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={draft.defaultSaleUnitPriceEur}
              onChange={(e) => {
                patch({ defaultSaleUnitPriceEur: e.target.value });
                setStepError(null);
              }}
            />
            <p className="field-hint">
              Optional · empty = the suggested price (cost from a priced delivery, target margin, IVA) · none until a priced delivery
            </p>
          </div>
        </>
      );
    }

    if (step === "extras") {
      return (
        <>
          {/*
            * Shown for EVERY category, not just the one the default came from.
            * Picking Flower pre-answers this (see `categoryDefaults`), but the
            * single-page form let any item be marked perishable and so must
            * this: hiding the control for non-flowers would quietly delete a
            * capability rather than move it.
            */}
          <div className="iw-statement">
            <span>
              {draft.isPerishable ? (
                <>
                  <b>Perishable.</b> Stock arrives through flower batch receive, so it ages by
                  batch.
                </>
              ) : (
                <>
                  <b>Not perishable.</b> Stock is received the ordinary way.
                </>
              )}
            </span>
            <button
              type="button"
              className="button plain"
              onClick={() => patch({ isPerishable: !draft.isPerishable })}
            >
              Change
            </button>
          </div>

          {extrasOpen ? null : (
            <button
              type="button"
              className="button tinted iw-block"
              onClick={() => setExtrasOpen(true)}
            >
              Add stock details
            </button>
          )}

          {extrasOpen ? (
            <div className="iw-disclosure">
              <div className="field">
                <label htmlFor="reorder">Reorder threshold</label>
                <input
                  id="reorder"
                  type="number"
                  min={0}
                  step="0.001"
                  inputMode="decimal"
                  value={draft.reorderThreshold}
                  onChange={(e) => patch({ reorderThreshold: e.target.value })}
                />
                <p className="field-hint">Warn me below this many {unitWord}s.</p>
              </div>

              {showsDimensions ? (
                <>
                  <div className="field">
                    <label htmlFor="diameterCm">Diameter (cm)</label>
                    <input
                      id="diameterCm"
                      type="number"
                      min={0}
                      max={10_000}
                      step="any"
                      inputMode="decimal"
                      value={draft.diameterCm}
                      onChange={(e) => patch({ diameterCm: e.target.value })}
                    />
                    <p className="field-hint">Opening / outer diameter for buckets and vases.</p>
                  </div>
                  <div className="field">
                    <label htmlFor="depthCm">Depth (cm)</label>
                    <input
                      id="depthCm"
                      type="number"
                      min={0}
                      max={10_000}
                      step="any"
                      inputMode="decimal"
                      value={draft.depthCm}
                      onChange={(e) => patch({ depthCm: e.target.value })}
                    />
                  </div>
                </>
              ) : null}

              <div className="field">
                <label htmlFor="sku">SKU</label>
                <input
                  id="sku"
                  type="text"
                  maxLength={64}
                  value={draft.sku}
                  onChange={(e) => patch({ sku: e.target.value })}
                />
              </div>

              <BarcodeField
                id="barcode"
                value={draft.barcode}
                onChange={(v) => patch({ barcode: v })}
              />
            </div>
          ) : null}
        </>
      );
    }

    // review
    const rows: { label: string; value: string; step: WizardStep }[] = [
      {
        label: "Category",
        value: creatingNewCategory
          ? `${draft.newCategoryLabel.trim()} (new)`
          : (selectedCategory?.label ?? ""),
        step: "category",
      },
      { label: "Name", value: draft.name.trim(), step: "name" },
      {
        label: "Flower type",
        value: subCategories.find((s) => s.id === draft.subCategoryId)?.label ?? "",
        step: "name",
      },
      {
        label: "Colour",
        value: colors.find((c) => c.id === draft.colorId)?.name ?? "",
        step: "name",
      },
      { label: "Photo", value: imageFile ? "Yes" : "", step: "photo" },
      { label: "Counted by", value: draft.defaultUom, step: "price" },
      {
        label: "You charge",
        value: draft.defaultSaleUnitPriceEur.trim()
          ? `€${draft.defaultSaleUnitPriceEur.trim()}`
          : "Suggested price after a priced delivery",
        step: "price",
      },
      {
        label: "Details",
        value: [
          draft.reorderThreshold.trim() ? `reorder at ${draft.reorderThreshold.trim()}` : "",
          draft.sku.trim() ? `SKU ${draft.sku.trim()}` : "",
          draft.barcode.trim() ? `barcode ${draft.barcode.trim()}` : "",
          showsDimensions && (draft.diameterCm.trim() || draft.depthCm.trim())
            ? `${draft.diameterCm.trim() || "?"} × ${draft.depthCm.trim() || "?"} cm`
            : "",
          draft.isPerishable ? "perishable" : "",
        ]
          .filter(Boolean)
          .join(", "),
        step: "extras",
      },
    ];

    return (
      <>
        {submitError ? <div className="alert error">{submitError}</div> : null}
        {duplicatePrompt ? (
          <DuplicateNameDialog
            name={duplicatePrompt.name}
            existing={duplicatePrompt.existing}
            confirmLabel="Create it anyway"
            busy={submitting}
            onConfirm={() => void createItem(duplicatePrompt.existing.map((e) => e.id))}
            onClose={() => setDuplicatePrompt(null)}
          />
        ) : null}
        <dl className="iw-receipt">
          {rows
            .filter((row) => row.label !== "Flower type" || subCategories.length > 0)
            .map((row) => (
              <button
                key={row.label}
                type="button"
                className="iw-receipt-row"
                onClick={() => goTo(row.step)}
              >
                <dt>{row.label}</dt>
                <dd className={row.value ? undefined : "iw-empty"}>{row.value || "none"}</dd>
              </button>
            ))}
        </dl>
      </>
    );
  }

  /* ── the done screen ──────────────────────────────────────────────────── */

  if (step === "done" && created) {
    const receiveTargetKnown = Boolean(receiveLocationId);
    return (
      <WizardShell current="done" furthestIndex={furthestIndex} onJump={goTo} title="In the catalog">
        <div className="iw-done">
          {photoUrl ? (
            <img className="iw-done-photo" src={photoUrl} alt={created.name} />
          ) : null}
          <div className={photoUrl ? "iw-done-mark iw-done-mark--small" : "iw-done-mark"} aria-hidden="true">
            ✓
          </div>
          <p className="iw-done-line">
            {created.name} is in the catalog, with no stock against it yet.
          </p>
          {receiveTargetKnown ? null : (
            <div className="field">
              <label htmlFor="receiveShop">Which shop?</label>
              <select
                id="receiveShop"
                value={receiveLocationId}
                onChange={(e) => setReceiveLocationId(e.target.value)}
              >
                <option value="">Choose a shop…</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button"
            className="button iw-block"
            disabled={!receiveLocationId}
            onClick={receiveInShop}
          >
            Receive in the shop
          </button>
          <button type="button" className="button tinted iw-block" onClick={startAnother}>
            Create another item
          </button>
          <button
            type="button"
            className="button plain iw-block"
            onClick={() => navigate("/app/items")}
          >
            Back to items
          </button>
        </div>
      </WizardShell>
    );
  }

  /* ── the bottom bar ───────────────────────────────────────────────────── */

  const currentStep = step as WizardStep;
  const onFirstStep = currentStep === "category";
  const onReview = currentStep === "review";
  // Screens where the forward move belongs to the body, not the bar: a tile on
  // the first screen, "Skip for now" on a photo screen with no photo yet.
  const barCarriesForward =
    !(onFirstStep && !creatingNewCategory) && !(currentStep === "photo" && !imageFile);

  const actions = (
    <>
      {onFirstStep ? null : (
        <button type="button" className="button plain" onClick={back}>
          Back
        </button>
      )}
      {!barCarriesForward ? (
        <span className="iw-bar-hint">
          {onFirstStep ? "Tap a category" : "Take a photo, or skip"}
        </span>
      ) : (
        <button
          type="button"
          className="button iw-grow"
          disabled={submitting}
          onClick={onReview ? () => void createItem() : advance}
        >
          {onReview ? (submitting ? "Creating…" : "Create item") : "Continue"}
        </button>
      )}
    </>
  );

  const TITLES: Record<WizardStep, { title: string; subtitle: string }> = {
    category: {
      title: "What is it?",
      subtitle: "The category sets the unit, the VAT rate, and what we ask you next.",
    },
    name: {
      title: "What is it called?",
      subtitle: "The name your florists will search for on the floor.",
    },
    photo: {
      title: "Take its picture",
      subtitle: "The photo is what everyone taps on the kiosk.",
    },
    price: {
      title: "Selling price",
      subtitle: selectedCategory
        ? `Counted by the ${unitWord}, ${selectedCategory.vatRatePercent}% VAT.`
        : "",
    },
    extras: {
      title: "Anything else?",
      subtitle: "All optional. Most items leave this screen untouched.",
    },
    review: { title: "Check and create", subtitle: "Tap any line to go back and change it." },
  };

  return (
    <WizardShell
      current={currentStep}
      furthestIndex={furthestIndex}
      onJump={goTo}
      title={TITLES[currentStep].title}
      subtitle={TITLES[currentStep].subtitle}
      actions={actions}
    >
      {stepError ? <div className="alert error">{stepError}</div> : null}
      {renderStep()}
    </WizardShell>
  );
}
